import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavigationItem {
  id: string;
  label: string;
  path: string;
  children?: NavigationItem[];
}

const Navigation: React.FC = () => {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: 'サービス', href: '#services' },
    { label: '会社概要', href: '#about' },
    { label: 'アプローチ', href: '#approach' },
    { label: '導入実績', href: '#results' },
    { label: 'お問い合わせ', href: '#contact' }
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-xl border-b-2 border-purple-100' 
          : 'bg-transparent'
      }`}>
        <div className="w-full px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* ロゴ */}
            <a 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-fuchsia-500 to-violet-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <i className="ri-sparkling-2-fill text-2xl text-white w-6 h-6 flex items-center justify-center"></i>
              </div>
              <span className={`text-2xl font-black ${isScrolled ? 'text-purple-900' : 'text-white'} transition-colors duration-300`} style={{ fontFamily: '"Shippori Mincho", serif' }}>
                SASAICHI
              </span>
            </a>

            {/* デスクトップメニュー */}
            <div className="hidden lg:flex items-center gap-2">
              {navItems.map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(item.href);
                  }}
                  className={`px-6 py-3 rounded-full font-black text-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    isScrolled
                      ? 'text-purple-900 hover:bg-purple-50'
                      : 'text-white hover:bg-white/10'
                  }`}
                  style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
                >
                  {item.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('#contact');
                }}
                className="ml-4 px-8 py-3 bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white rounded-full font-black text-sm transition-all duration-300 hover:scale-110 hover:shadow-xl active:scale-95 shadow-lg border-2 border-white/20 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
              >
                お問い合わせ
              </a>
            </div>

            {/* モバイルメニューボタン */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                isScrolled
                  ? 'bg-purple-50 text-purple-900'
                  : 'bg-white/10 text-white backdrop-blur-md'
              }`}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl w-6 h-6 flex items-center justify-center`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* モバイルメニュー */}
      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
        isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}>
        <div 
          className="absolute inset-0 bg-purple-900/95 backdrop-blur-lg"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
        <div className="absolute top-20 left-0 right-0 p-6">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-purple-100">
            {navItems.map((item, index) => (
              <a
                key={index}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.href);
                }}
                className="block px-8 py-5 text-purple-900 font-black text-lg border-b border-purple-100 last:border-b-0 hover:bg-purple-50 transition-colors duration-300 cursor-pointer"
                style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
              >
                {item.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#contact');
              }}
              className="block m-6 px-8 py-5 bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white text-center rounded-full font-black text-lg transition-all duration-300 hover:scale-105 shadow-lg cursor-pointer"
              style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
            >
              お問い合わせ
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
